import { TABLE_SORTER_TOOLTIP } from '../../../shared/ui/list-styles.js';
// UI-only detail panel for the admin workspaces Master-Detail screen.
// No data fetching: the page passes the detail query result and mutation
// callbacks (cf. TemplateEditor on the Templates screen).
//
// Block asks for an optional reason (goes to the audit metadata) and warns
// that members lose access; unblock restores both scope resolution and the
// background workers instantly (data is never deleted).
import { useState, type ReactElement } from 'react';
import {
  Badge,
  Button,
  Card,
  Descriptions,
  Empty,
  Input,
  Popconfirm,
  Skeleton,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { StopOutlined, UnlockOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

import type {
  AdminPlanConfig,
  AdminWorkspaceDetail,
  AdminWorkspaceMemberRow,
} from '../api/admin.client.js';
import { ApiError } from '../../../shared/api-client/index.js';
import {
  formatAdminCount,
  formatAdminSpend,
  formatDate,
  formatPlanStorage,
} from '../lib/format.js';

const { Text } = Typography;

type Translate = (key: string) => string;

/** Maps known admin API error codes to localized messages. */
function actionErrorMessage(err: unknown, t: Translate): string {
  if (err instanceof ApiError && err.code === 'not_platform_admin') {
    return t('crm.admin.errors.not_platform_admin');
  }
  return t('crm.admin.errors.action_failed');
}

export interface WorkspaceDetailPanelProps {
  detail: AdminWorkspaceDetail | null;
  isLoading: boolean;
  /** Disables actions while a mutation is in flight. */
  pending: boolean;
  /** Plan config matching `detail.workspace.plan`; powers the limit hints. */
  planConfig: AdminPlanConfig | null;
  onBlock: (workspaceId: string, reason?: string) => Promise<unknown>;
  onUnblock: (workspaceId: string) => Promise<unknown>;
}

async function runGuarded(action: () => Promise<unknown>, t: Translate): Promise<void> {
  try {
    await action();
  } catch (rejection: unknown) {
    void message.error(actionErrorMessage(rejection, t));
  }
}

export function WorkspaceDetailPanel({
  detail,
  isLoading,
  pending,
  planConfig,
  onBlock,
  onUnblock,
}: WorkspaceDetailPanelProps): ReactElement {
  const { t } = useTranslation();
  const [blockReason, setBlockReason] = useState('');

  const memberColumns: Array<ColumnType<AdminWorkspaceMemberRow>> = [
    {
      title: t('crm.admin.workspaces.col_member_user'),
      dataIndex: ['user', 'email'],
      key: 'user',
      ellipsis: true,
      render: (value: string, row) => (
        <div className="flex min-w-0 flex-col">
          <Text style={{ fontSize: 12 }} ellipsis>
            {row.user.name}
          </Text>
          <Text className="font-mono" style={{ fontSize: 11 }} type="secondary" ellipsis>
            {value}
          </Text>
        </div>
      ),
    },
    {
      title: t('crm.admin.workspaces.col_member_role'),
      dataIndex: 'role',
      key: 'role',
      width: 90,
      render: (value: string) => <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>{value}</Tag>,
    },
    {
      title: t('crm.admin.workspaces.col_member_status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: string) => (
        <Badge
          status={value === 'active' ? 'success' : 'default'}
          text={<span style={{ fontSize: 11 }}>{value}</span>}
        />
      ),
    },
    {
      title: t('crm.admin.workspaces.col_member_joined'),
      dataIndex: 'joinedAt',
      key: 'joinedAt',
      width: 110,
      render: (value: string) => (
        <Text style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
          {formatDate(value)}
        </Text>
      ),
    },
  ];

  return (
    <Card
      size="small"
      className="lg:sticky lg:top-0"
      styles={{ body: { padding: 16 } }}
      data-testid="admin-workspace-panel"
    >
      {detail === null ? (
        isLoading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t('crm.admin.workspaces.detail_empty')}</Text>}
          />
        )
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <Text strong style={{ color: 'var(--ant-color-text)', fontSize: 15 }}>
                {detail.workspace.name}
              </Text>
              <br />
              <Text className="font-mono" type="secondary" style={{ fontSize: 12 }}>
                {detail.workspace.slug}
              </Text>
            </div>
            <div className="flex flex-col items-end gap-1">
              <EntityStatusChip style={{ marginInlineEnd: 0, fontSize: 11 }}>
                {detail.workspace.kind === 'personal'
                  ? t('crm.admin.workspaces.kind_personal')
                  : t('crm.admin.workspaces.kind_business')}
              </EntityStatusChip>
              {detail.workspace.isBlocked ? (
                <Badge
                  status="error"
                  text={
                    <span style={{ fontSize: 11 }}>{t('crm.admin.workspaces.status_blocked')}</span>
                  }
                />
              ) : (
                <Badge
                  status="success"
                  text={
                    <span style={{ fontSize: 11 }}>{t('crm.admin.workspaces.status_active')}</span>
                  }
                />
              )}
            </div>
          </div>

          <Descriptions
            size="small"
            column={1}
            // antd 6: labelStyle/contentStyle → styles.label/styles.content.
            styles={{ label: { fontSize: 12 }, content: { fontSize: 12 } }}
            items={[
              {
                key: 'owner',
                label: t('crm.admin.workspaces.col_owner'),
                children: detail.workspace.owner
                  ? `${detail.workspace.owner.name} · ${detail.workspace.owner.email}`
                  : '—',
              },
              {
                key: 'plan',
                label: t('crm.admin.workspaces.detail_plan'),
                children:
                  planConfig === null ? (
                    detail.workspace.plan
                  ) : (
                    <div className="flex min-w-0 flex-col">
                      <span>
                        {detail.workspace.plan} · {planConfig.displayName}
                      </span>
                      <span
                        className="font-mono"
                        style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)' }}
                      >
                        {t('crm.admin.workspaces.detail_plan_limits', {
                          tokens: formatAdminCount(Number(planConfig.tokenLimitMonthly)),
                          storage: formatPlanStorage(planConfig.storageLimitBytes),
                        })}
                      </span>
                    </div>
                  ),
              },
              {
                key: 'timezone',
                label: t('crm.admin.workspaces.detail_timezone'),
                children: detail.workspace.timezone,
              },
              {
                key: 'created',
                label: t('crm.admin.workspaces.detail_created'),
                children: formatDate(detail.workspace.createdAt),
              },
            ]}
          />

          <div className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
            {(
              [
                ['counter_deals', detail.counters.deals],
                ['counter_contacts', detail.counters.contacts],
                ['counter_tasks', detail.counters.tasks],
                ['counter_messages', detail.counters.messages],
                ['counter_active_members', detail.counters.activeMembers],
              ] as const
            ).map(([key, count]) => (
              <div key={key} className="flex items-baseline justify-between gap-2">
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t(`crm.admin.workspaces.${key}`)}
                </Text>
                <Text className="font-mono" style={{ fontSize: 12 }}>
                  {formatAdminCount(count)}
                </Text>
              </div>
            ))}
            <div className="flex items-baseline justify-between gap-2">
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t('crm.admin.workspaces.detail_spend')}
              </Text>
              <Text className="font-mono font-semibold" style={{ fontSize: 12 }}>
                {formatAdminSpend(detail.spend.costRub)}
              </Text>
            </div>
          </div>

          <div>
            <Text
              className="mb-2 block text-[11px] font-medium uppercase tracking-widest"
              style={{ color: 'var(--ant-color-text-secondary)' }}
            >
              {t('crm.admin.workspaces.detail_members')} ({detail.members.length})
            </Text>
            <Table<AdminWorkspaceMemberRow>
              showSorterTooltip={TABLE_SORTER_TOOLTIP}
              size="small"
              rowKey="id"
              columns={memberColumns}
              dataSource={detail.members}
              pagination={false}
              scroll={{ y: 240 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('crm.admin.workspaces.detail_no_members')}
                  />
                ),
              }}
            />
          </div>

          {detail.workspace.isBlocked ? (
            <Popconfirm
              title={t('crm.admin.workspaces.action_unblock')}
              description={t('crm.admin.workspaces.confirm_unblock')}
              okText={t('crm.admin.workspaces.action_unblock')}
              cancelText={t('crm.common.cancel')}
              okButtonProps={{ loading: pending }}
              onConfirm={() => runGuarded(() => onUnblock(detail.workspace.id), t)}
            >
              <Button icon={<UnlockOutlined />} block loading={pending}>
                {t('crm.admin.workspaces.action_unblock')}
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title={t('crm.admin.workspaces.action_block')}
              description={
                <div className="min-w-[260px]">
                  <p className="mb-2 text-xs text-[var(--ant-color-text-secondary)]">
                    {t('crm.admin.workspaces.confirm_block', { name: detail.workspace.name })}
                  </p>
                  <Input
                    aria-label={t('crm.admin.workspaces.block_reason_label')}
                    placeholder={t('crm.admin.workspaces.block_reason_placeholder')}
                    value={blockReason}
                    maxLength={500}
                    onChange={(event) => setBlockReason(event.target.value)}
                  />
                </div>
              }
              okText={t('crm.admin.workspaces.action_block')}
              okButtonProps={{ danger: true, loading: pending }}
              cancelText={t('crm.common.cancel')}
              onConfirm={() => {
                const reason = blockReason.trim();
                setBlockReason('');
                return runGuarded(
                  () => onBlock(detail.workspace.id, reason.length > 0 ? reason : undefined),
                  t,
                );
              }}
            >
              <Button icon={<StopOutlined />} danger block loading={pending}>
                {t('crm.admin.workspaces.action_block')}
              </Button>
            </Popconfirm>
          )}
        </div>
      )}
    </Card>
  );
}
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
